// Pure model of the automation rules admin UI (spec 05 §7). The SPA imports the shared Deno
// modules directly: they are plain TypeScript with relative `.ts` imports and no Deno globals.
import type {
  ActionType,
  AutomationRule,
  Trigger,
} from "../../../../supabase/functions/_shared/automation/types.ts";
import {
  planRule,
  type PlanEvent,
} from "../../../../supabase/functions/_shared/automation/planner.ts";
import {
  validateRule,
  type Issue,
} from "../../../../supabase/functions/_shared/automation/validateRule.ts";
import type { GavionConfig } from "../domain/config";

/** The configured values, then any current value not among them (a stage since deleted: kept, shown raw). */
export const withCurrent = (
  configured: readonly string[],
  current: readonly string[],
): string[] => [
  ...configured,
  ...current.filter((v) => v !== "" && !configured.includes(v)),
];

export const TRIGGERS: readonly Trigger[] = [
  "call_outcome",
  "deal_stage_changed",
  "deal_created",
  "no_activity",
  "email_event",
  "meeting_note_matched",
  "schedule",
];

/** Every code validateRule can emit; each one has a message in automation.{fr,en}.ts. */
export const ERROR_CODES = [
  "required",
  "invalid_rule",
  "conditions_required",
  "too_deep",
  "unknown_op",
  "invalid_regex",
  "unsafe_regex",
  "actions_not_array",
  "unknown_action",
  "invalid_business_days",
  "unknown_template",
] as const;
export const WARNING_CODES = ["unknown_field", "prefer_graph"] as const;

export type ParamDef = {
  key: string;
  kind:
    | "number"
    | "time"
    | "text"
    | "template"
    | "enum"
    | "stage" // a configured deal stage
    | "lead_status" // a configured lead status
    | "delay"
    | "channels";
  values?: readonly string[];
};
export const ACTION_PARAMS: Record<ActionType, ParamDef[]> = {
  set_callback: [
    { key: "business_days", kind: "number" },
    { key: "at", kind: "time" },
  ],
  set_prospect_status: [{ key: "status", kind: "lead_status" }],
  cool_down: [{ key: "days", kind: "number" }],
  create_task: [
    {
      key: "task_type",
      kind: "enum",
      values: ["call", "demo", "follow_up", "email"],
    },
    { key: "text", kind: "template" },
    { key: "due", kind: "delay" },
    { key: "assignee", kind: "enum", values: ["rep", "admin"] },
  ],
  create_deal: [
    { key: "stage", kind: "stage" },
    { key: "name_template", kind: "template" },
    { key: "amount_from", kind: "text" },
  ],
  move_deal: [{ key: "stage", kind: "stage" }],
  add_note: [{ key: "text_template", kind: "template" }],
  enroll_sequence: [{ key: "sequence_id", kind: "number" }],
  stop_sequences: [{ key: "reason", kind: "text" }],
  send_email: [
    { key: "template", kind: "text" },
    { key: "via", kind: "enum", values: ["graph", "ses"] },
    {
      key: "to",
      kind: "enum",
      values: ["company.email", "contact.email", "call.email"],
    },
  ],
  create_calendar_event: [{ key: "from", kind: "enum", values: ["call.demo"] }],
  notify_teams: [
    {
      key: "template",
      kind: "enum",
      values: ["demo_booked", "deal_won", "deal_lost"],
    },
  ],
  notify_user: [
    { key: "who", kind: "enum", values: ["rep", "admins"] },
    { key: "message_template", kind: "template" },
  ],
  suppress: [{ key: "channels", kind: "channels" }],
  // GAVION ai-email
  ai_draft: [
    {
      key: "trigger",
      kind: "enum",
      values: ["after_call", "client_reply", "after_demo", "stale_lead"],
    },
  ],
};

export type EditableRule = Omit<AutomationRule, "id"> & { id?: number };

export const emptyConditions = (trigger: Trigger): unknown =>
  trigger === "no_activity"
    ? { entity: "deal", days: 7, stage_in: [] }
    : trigger === "schedule"
      ? { cron: "0 9 * * 1-5" }
      : { all: [] };

export const emptyRule = (trigger: Trigger): EditableRule => ({
  name: "",
  description: "",
  trigger,
  conditions: emptyConditions(trigger),
  actions: [],
  enabled: true,
  position: 1000,
  is_default: false,
});

export const toRule = (draft: EditableRule): AutomationRule => ({
  ...draft,
  id: draft.id ?? 0,
});

/** validateRule never throws; this only adapts the draft (no id yet) to its input type. */
export const validateDraft = (
  draft: EditableRule,
  knownTemplates: readonly string[] | null,
): { errors: Issue[]; warnings: Issue[] } =>
  validateRule(toRule(draft), { knownTemplates });

/** The regex check of validateRule, applied to a single `matches` value (for inline display). */
export function regexIssue(
  pattern: unknown,
): "invalid_regex" | "unsafe_regex" | null {
  const probe = toRule({
    ...emptyRule("call_outcome"),
    name: "probe",
    conditions: { field: "call.note", op: "matches", value: pattern },
  });
  const hit = validateRule(probe, { knownTemplates: null }).errors.find(
    (e) => e.code === "invalid_regex" || e.code === "unsafe_regex",
  );
  return (hit?.code as "invalid_regex" | "unsafe_regex" | undefined) ?? null;
}

const POSITION_STEP = 10;

/**
 * Moves index `from` to `to` and renumbers the whole list 10, 20, 30… so ties and gaps never
 * survive a reorder. Returns only the rules whose position changes.
 */
export function reorderPositions<T extends { id: number; position: number }>(
  rules: readonly T[],
  from: number,
  to: number,
): { id: number; position: number }[] {
  if (from === to) return [];
  const moved = [...rules];
  const [item] = moved.splice(from, 1);
  moved.splice(to, 0, item);
  return moved
    .map((r, i) => ({
      id: r.id,
      position: (i + 1) * POSITION_STEP,
      was: r.position,
    }))
    .filter((r) => r.was !== r.position)
    .map(({ id, position }) => ({ id, position }));
}

/** Position of a new rule: after the last one. */
export const nextPosition = (rules: readonly { position: number }[]): number =>
  rules.reduce((max, r) => Math.max(max, r.position), 0) + POSITION_STEP;

/** True when the conditions hold nothing worth confirming before a trigger change resets them. */
export function conditionsEmpty(trigger: Trigger, c: unknown): boolean {
  if (c === null || c === undefined || typeof c !== "object") return true;
  const node = c as Record<string, unknown>;
  const kids = node.all ?? node.any;
  if (Array.isArray(kids)) return kids.length === 0;
  if (Object.keys(node).length === 0) return true;
  return JSON.stringify(c) === JSON.stringify(emptyConditions(trigger));
}

/** Action param keys that have a label in `gavion.automation.params.*`. */
export const PARAM_KEYS: ReadonlySet<string> = new Set(
  Object.values(ACTION_PARAMS).flatMap((defs) => defs.map((d) => d.key)),
);

export type RuleStats = {
  lastRunAt: string | null;
  lastStatus: string | null;
  runs7d: number;
  failures7d: number;
};
export function ruleStats(
  runs: readonly { rule_id: number; status: string; created_at: string }[],
  now: Date,
): Record<number, RuleStats> {
  const since = now.getTime() - 7 * 86_400_000;
  const out: Record<number, RuleStats> = {};
  for (const r of runs) {
    const prev = out[r.rule_id] ?? {
      lastRunAt: null,
      lastStatus: null,
      runs7d: 0,
      failures7d: 0,
    };
    const latest = !prev.lastRunAt || r.created_at > prev.lastRunAt;
    const recent = Date.parse(r.created_at) >= since;
    out[r.rule_id] = {
      lastRunAt: latest ? r.created_at : prev.lastRunAt,
      lastStatus: latest ? r.status : prev.lastStatus,
      runs7d: prev.runs7d + (recent ? 1 : 0),
      failures7d: prev.failures7d + (recent && r.status === "failed" ? 1 : 0),
    };
  }
  return out;
}

/** "Tester la règle" uses the exact planner the tick uses. Pure: nothing is written or executed. */
export const computeDryRun = (
  rule: AutomationRule,
  event: PlanEvent,
  config: GavionConfig,
  now: Date,
) => planRule(rule, event, { now, config });

const CODE = /^[a-z0-9_.:-]{1,64}$/i;
/** History shows codes only: a code-shaped string, "error" for anything else, null when absent. */
export const safeCode = (v: unknown): string | null =>
  v === null || v === undefined || v === ""
    ? null
    : typeof v === "string" && CODE.test(v)
      ? v
      : "error";

export type RunAction = {
  index: number;
  type: string;
  status: string;
  code: string | null;
};
/** Per-action summary of automation_runs.detail; payloads and traces are never shown. */
export function runActions(detail: unknown): RunAction[] {
  const actions = (detail as { actions?: unknown } | null)?.actions;
  if (!Array.isArray(actions)) return [];
  return actions.map((a, i) => {
    const x = (a ?? {}) as Record<string, unknown>;
    return {
      index: typeof x.index === "number" ? x.index : i,
      type: safeCode(x.type) ?? "error",
      status: safeCode(x.status) ?? "error",
      code: safeCode(x.error ?? x.reason),
    };
  });
}
